import { useEffect, useState } from 'react'

import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useOutletContext,
  useRevalidator,
  useRouteError,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix'
import { createBrowserClient } from '@supabase/ssr'
import { json } from '@vercel/remix'
import { SpeedInsights } from '@vercel/speed-insights/remix'
import posthog from 'posthog-js'

import { isPostgrestError } from '@/utils/variables'
import FitError from '~/components/fit-error'
import { Toaster } from '~/components/ui/toaster'
import { createServerClient } from '~/models/supabase.server'
import styles from '~/tailwind.css?url'

import type { AvatarStorages, KbStorages, TCStorages } from './typings'
import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from '@vercel/remix'
import type { Database } from 'utils/supabase'

export const meta: MetaFunction = () => [
  {
    title: 'Cozee - Collaborative Intelligence Tool',
  },
]

export const links: LinksFunction = () => [{ rel: 'stylesheet', href: styles }]

type ContextType = { supabase: SupabaseClient<any, 'public', any> }

export const loader = async ({ request }: LoaderFunctionArgs) => {
  /// We can retrieve the session on the server and hand it to the client.
  // This is used to make sure the session is available immediately upon rendering
  const { supabase, headers } = createServerClient({ request })

  // Get the session for token info
  const {
    data: { session },
  } = await supabase.auth.getSession()

  // Get the verified user separately
  const {
    data: { user },
  } = await supabase.auth.getUser()

  // Create a safe session object with only necessary data
  const safeSession = session
    ? {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_in: session.expires_in,
        expires_at: session.expires_at,
        token_type: session.token_type,
        user: user, // Use the verified user instead of session.user
      }
    : null

  // in order for the set-cookie header to be set,
  // headers must be returned as part of the loader response
  return json(
    {
      env: {
        SUPABASE_URL: process.env.SUPABASE_URL!,
        SUPABASE_KEY: process.env.SUPABASE_KEY!,
        SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT!,
        KB_BUCKET: process.env.KNOWLEDGE_BASE_BUCKET,
        TC_BUCKET: process.env.TASK_CENTER_BUCKET,
        AVATAR_BUCKET: process.env.AVATAR_BUCKET || 'avatars',
        PUSHER_KEY: process.env.PUSHER_KEY,
        PUSHER_CLUSTER: process.env.PUSHER_CLUSTER,
      },
      session: safeSession,
    },
    {
      headers,
    }
  )
}

declare global {
  interface Window {
    intercomSettings: {
      app_id: string
      email: string
      user_id: string
      api_base: string
    }
    Intercom: any
    env: {
      SENTRY_ENVIRONMENT: string
      KB_BUCKET: KbStorages
      TC_BUCKET: TCStorages
      AVATAR_BUCKET: AvatarStorages
      PUSHER_KEY: string
      PUSHER_CLUSTER: string
    }
  }
}

export function ErrorBoundary() {
  const error = useRouteError()

  let errorMessage =
    'This page either does not exist or you do not have permission to view it'

  if (isRouteErrorResponse(error) && error.data) {
    errorMessage = error.data.message ?? error.data
  }
  if (isPostgrestError(error) && error.message) {
    errorMessage = error.message
  }

  captureRemixErrorBoundaryError(error)

  return (
    <html>
      <head>
        <title>Oops!</title>
        <Meta />
        <Links />
      </head>
      <body>
        <FitError errorMessage={errorMessage} userData={null} />
        <Scripts />
      </body>
    </html>
  )
}

function App() {
  const { env, session } = useLoaderData<typeof loader>()

  const { revalidate } = useRevalidator()
  const [supabase] = useState<SupabaseClient>(() => {
    return createBrowserClient<Database>(env.SUPABASE_URL, env.SUPABASE_KEY)
  })
  const serverAccessToken = session?.access_token
  const location = useLocation()

  useEffect(() => {
    posthog.capture('$pageview')
  }, [location])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    import('preline')
  }, [])

  useEffect(() => {
    // Register Firebase service worker for push notifications
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
    }
  }, [])

  useEffect(() => {
    const exploScript = document.createElement('script')
    if (session) {
      // append explo script to head if session exists. If user logs out, remove script
      exploScript.src = 'https://embed.explo.co/bundle.js'
      document.head.append(exploScript)
    } else {
      exploScript.remove()
    }
  }, [session])

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event !== 'INITIAL_SESSION' &&
        session?.access_token !== serverAccessToken
      ) {
        // server and client are out of sync.
        revalidate()
      }
      if (event === 'SIGNED_OUT') {
        // delete cookies on sign out
        const expires = new Date(0).toUTCString()
        document.cookie = `my-access-token=; path=/; expires=${expires}; SameSite=Lax; secure`
        document.cookie = `my-refresh-token=; path=/; expires=${expires}; SameSite=Lax; secure`
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        const maxAge = 100 * 365 * 24 * 60 * 60 // 100 years, never expires
        document.cookie = `my-access-token=${session?.access_token}; path=/; max-age=${maxAge}; SameSite=Lax; secure`
        document.cookie = `my-refresh-token=${session?.refresh_token}; path=/; max-age=${maxAge}; SameSite=Lax; secure`
      }
    })
    return () => {
      subscription.unsubscribe()
    }
  }, [serverAccessToken, supabase])

  return (
    <html lang='en'>
      <head>
        <meta httpEquiv='Content-Type' content='text/html;charset=utf-8' />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
        <Meta />
        <Links />
      </head>
      <body>
        <Outlet context={{ supabase }} />
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(env)}`,
          }}
        />
        <Scripts />
        <Toaster />
        <SpeedInsights />
      </body>
    </html>
  )
}

export default withSentry(App)

export function useRootContext() {
  return useOutletContext<ContextType>()
}
